import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, ItemRow, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { useItem } from "../../lib/data.ts";
import { showDark } from "../../lib/documents.ts";
import { linkify, mailDate, mailText, parseEmail } from "../../lib/emailText.ts";
import { formatInstant } from "../../lib/format.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useResource } from "../../lib/resource.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Chevron } from "../../ui/Chevron.tsx";
import { DarkToggle } from "../../ui/DarkToggle.tsx";
import { Async } from "../../ui/misc.tsx";
import { LINK } from "../../ui/styles.ts";

/** Most emails of a thread listed. */
const THREAD_MAX = 200;

/** Text with its URLs as links (opening in a new tab). */
function Linked({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((r, i) =>
        typeof r === "string" ? (
          r
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: runs of one text, in order, never reordered.
          <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" class={`${LINK} break-all`}>
            {r.label}
          </a>
        ),
      )}
    </>
  );
}

/** One line of the header: its name, and the value (nothing when empty). */
function HeaderLine({ label, value }: { label: string; value: string }) {
  return value ? (
    <div class="flex gap-3">
      <dt class="w-12 shrink-0 text-zinc-500">{label}</dt>
      <dd class="min-w-0 break-words text-zinc-800">{value}</dd>
    </div>
  ) : null;
}

/** What an email's page says of itself, by message (its script, server/src/email_html.rs). */
type PageSays = { revisaEmail: 1; height: number; remote: number; quoted: number };

function isPageSays(d: unknown): d is PageSays {
  const p = d as Partial<PageSays> | null;
  return typeof p === "object" && p !== null && p.revisaEmail === 1 && typeof p.height === "number";
}

/** An email page's height until it says (a few lines). */
const FRAME_MIN = 80;

/**
 * An email as its sender laid it out: its page (server/src/email_html.rs) framed sandboxed, from the files origin: no
 * scripts of its own, nothing of the app's within its reach, links opening in a new tab. As tall as it is (the page
 * says). Pictures from the web (tracking ones, often) and the earlier mail it quotes only when asked. On a dark app,
 * the moon turns its colours as a PDF's are (its pictures only dimmed), the same choice as theirs.
 */
function HtmlBody({ url, onText }: { url: string; onText: () => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(FRAME_MIN);
  const [left, setLeft] = useState({ remote: 0, quoted: 0 });
  const [remote, setRemote] = useState(false);
  const [quoted, setQuoted] = useState(false);
  useEffect(() => {
    const said = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow || !isPageSays(e.data)) {
        return;
      }
      setHeight(Math.max(FRAME_MIN, e.data.height));
      setLeft({ remote: e.data.remote, quoted: e.data.quoted });
    };
    window.addEventListener("message", said);
    return () => window.removeEventListener("message", said);
  }, []);
  const dark = showDark();
  const src = `${url}${remote ? "&remote=true" : ""}${quoted ? "&quoted=true" : ""}${dark ? "&dark=true" : ""}`;
  return (
    <div>
      <div class="flex flex-wrap items-center justify-end gap-2 px-4 pt-2">
        {left.remote > 0 && !remote ? (
          <Button size="xs" variant="ghost" onClick={() => setRemote(true)}>
            {t("email.showImages")}
          </Button>
        ) : null}
        {left.quoted > 0 ? (
          <Button size="xs" variant="ghost" aria-expanded={quoted} onClick={() => setQuoted(!quoted)}>
            {quoted ? t("email.hideQuoted") : t("email.showQuoted")}
          </Button>
        ) : null}
        <Button size="xs" variant="ghost" onClick={onText}>
          {t("email.asText")}
        </Button>
        <DarkToggle />
      </div>
      <iframe
        ref={frame}
        src={src}
        title={t("email.frame")}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        referrerpolicy="no-referrer"
        class="block w-full border-0"
        // The page's own ground while it loads: no white flash in dark colours (email_html.rs's DARK).
        style={{ height: `${height}px`, background: dark ? "#1b1b1b" : "#fff" }}
      />
    </div>
  );
}

/**
 * An email as a mail client shows it: who sent it to whom and when, then what the sender wrote. Written in HTML, as
 * they laid it out ([`HtmlBody`]); else (or asked) its text: their signature dimmed, and the earlier thread it quotes
 * folded away until asked for (open, for a forward of only that), only ever as text (links made links), so it shows
 * mail from anyone (held from unknown senders too) safely.
 */
export function EmailView({
  item,
  url,
  actions,
  onFold,
  aligned = false,
}: {
  item: Pick<ItemDetail, "text" | "data"> & { html_url?: string | null };
  url: string | undefined;
  /** More beside the download (in a conversation: going to its page). */
  actions?: ComponentChildren;
  /** In a conversation: folds it back to a line (a chevron on the left, as on the line). */
  onFold?: () => void;
  /** In a conversation but not foldable (the one shown): room where the chevron goes, to line up. */
  aligned?: boolean;
}) {
  const mail = parseEmail(item.text ?? "");
  // A forward with nothing of its own: the thread is what there is to read.
  const [thread, setThread] = useState(!mail.body);
  const [asText, setAsText] = useState(false);
  const html = asText ? null : item.html_url;
  const data = item.data ?? {};
  const date = mailDate(data);
  return (
    <article class="overflow-hidden rounded-md border border-zinc-200 bg-white">
      <header class="flex items-start gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-3">
        {onFold ? (
          <Chevron open={true} label={t("email.fold")} onToggle={onFold} class="mt-px" />
        ) : aligned ? (
          <span class="size-5 shrink-0" />
        ) : null}
        <dl class="flex min-w-0 flex-1 flex-col gap-1 text-sm" data-selectable="">
          <HeaderLine label={t("email.from")} value={mailText(data["from"])} />
          <HeaderLine label={t("email.to")} value={mailText(data["to"])} />
          <HeaderLine label={t("email.cc")} value={mailText(data["cc"])} />
          <HeaderLine label={t("email.date")} value={date ? formatInstant(date) : ""} />
        </dl>
        <span class="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 text-sm">
          {actions}
          {url ? (
            <a href={url} download={true} class={LINK}>
              {t("email.download")}
            </a>
          ) : null}
        </span>
      </header>
      {html ? (
        <HtmlBody url={html} onText={() => setAsText(true)} />
      ) : (
        <div class="px-4 py-4 font-serif text-[15px] leading-relaxed text-zinc-900" data-selectable="">
          {item.html_url ? (
            <div class="mb-2 flex justify-end font-sans">
              <Button size="xs" variant="ghost" onClick={() => setAsText(false)}>
                {t("email.asSent")}
              </Button>
            </div>
          ) : null}
          {mail.body ? (
            <p class="break-words whitespace-pre-wrap">
              <Linked text={mail.body} />
            </p>
          ) : mail.quoted ? null : (
            <p class="text-zinc-500">{t("email.empty")}</p>
          )}
          {mail.signature ? (
            <p class="mt-4 break-words whitespace-pre-wrap text-zinc-500">
              <Linked text={mail.signature} />
            </p>
          ) : null}
          {mail.quoted ? (
            <div class={mail.body ? "mt-4" : ""}>
              <Button
                size="xs"
                variant="ghost"
                class="font-sans"
                aria-expanded={thread}
                onClick={() => setThread(!thread)}
              >
                {thread ? t("email.hideQuoted") : t("email.showQuoted")}
              </Button>
              {thread ? (
                <p class="mt-2 border-l-2 border-zinc-200 pl-3 break-words whitespace-pre-wrap text-zinc-600">
                  <Linked text={mail.quoted} />
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </article>
  );
}

/** How far each email of a stack goes over the one before (the space its bottom keeps for it). */
const OVERLAP = "-mt-2";

/** A conversation's emails shown before folding the middle away (the first, and the two before this one). */
const AROUND = 2;

/** The start of what an email's sender wrote (not what it quotes), on one line. */
const firstWords = (snippet: string) => parseEmail(snippet).body.replace(/\s+/g, " ").trim();

/** Another email of the conversation: one line (who, what, when) until opened, then read in place. */
function Other({ space, row }: { space: SpaceOut; row: ItemRow }) {
  const [open, setOpen] = useState(false);
  const date = mailDate(row.data);
  if (open) {
    return <OtherOpen space={space} row={row} onFold={() => setOpen(false)} />;
  }
  return (
    <button
      type="button"
      aria-expanded={false}
      title={t("email.unfold")}
      onClick={() => setOpen(true)}
      class="flex w-full items-center gap-2 rounded-md border border-zinc-200 bg-white px-4 pt-2.5 pb-[18px] text-left text-sm hover:bg-zinc-50 focus-visible:outline-2 focus-visible:outline-blue-600"
    >
      <Chevron open={false} label={t("email.unfold")} />
      <span class="w-44 shrink-0 truncate font-medium text-zinc-800">{mailText(row.data?.["from"])}</span>
      <span class="min-w-0 flex-1 truncate text-zinc-500">{firstWords(row.snippet ?? "") || row.title}</span>
      <span class="shrink-0 text-xs text-zinc-500 tabular-nums">{date ? formatInstant(date) : ""}</span>
    </button>
  );
}

/** Another email, opened in place: the whole of it, with a link to its page and a way to fold it again. */
function OtherOpen({ space, row, onFold }: { space: SpaceOut; row: ItemRow; onFold: () => void }) {
  const detail = useItem(space, row.id);
  const actions = (
    <Link href={spaceUrl(space, `/items/${row.id}`)} class={LINK}>
      {t("email.openPage")}
    </Link>
  );
  return (
    <Async r={detail}>
      {(d) => <EmailView item={d} url={d.file_url ?? undefined} actions={actions} onFold={onFold} />}
    </Async>
  );
}

/**
 * An email with its conversation, as mail clients show one: the thread's emails in the order they were sent, this
 * one open, the others a line each that opens in place; a long thread folds its middle. Alone, just the email.
 */
export function EmailConversation({
  space,
  item,
  url,
}: {
  space: SpaceOut;
  item: ItemDetail;
  url: string | undefined;
}) {
  return item.thread ? (
    <EmailThread space={space} item={item} url={url} thread={item.thread} />
  ) : (
    <EmailView item={item} url={url} />
  );
}

function EmailThread({
  space,
  item,
  url,
  thread,
}: {
  space: SpaceOut;
  item: ItemDetail;
  url: string | undefined;
  thread: string;
}) {
  const [all, setAll] = useState(false);
  const list = useResource(`thread:${space.id}:${thread}`, (signal) =>
    api.get<PageItemRow>(spaceApi(space, "/items"), { thread, sort: "data.date", limit: THREAD_MAX }, signal),
  );
  const rows = list.data?.data ?? [];
  const here = rows.findIndex((r) => r.id === item.id);
  if (rows.length < 2 || here < 0) {
    return <EmailView item={item} url={url} />;
  }
  // The first email, then (unless asked for all) the middle folded, then from just before this one on.
  const from = Math.max(1, here - AROUND);
  const folded = all ? 0 : from - 1;
  const shown = folded > 1 ? [rows[0], ...rows.slice(from)] : rows;
  const cards = shown.flatMap((r, i) => {
    if (r === undefined) {
      return [];
    }
    const card =
      r.id === item.id
        ? {
            key: r.id,
            node: (
              <div class="rounded-md ring-2 ring-blue-200">
                <EmailView item={item} url={url} aligned={true} />
              </div>
            ),
          }
        : { key: r.id, node: <Other space={space} row={r} /> };
    const more =
      i === 0 && folded > 1
        ? [
            {
              key: "more",
              node: (
                <button
                  type="button"
                  onClick={() => setAll(true)}
                  class={
                    "w-full rounded-md border border-zinc-200 bg-zinc-50 px-4 pt-2 pb-4 text-left text-xs text-zinc-600 hover:bg-zinc-100"
                  }
                >
                  {t("email.more", { count: folded })}
                </button>
              ),
            },
          ]
        : [];
    return [card, ...more];
  });
  return (
    <section aria-label={t("email.thread", { count: rows.length })} class="flex flex-col">
      <p class="mb-2 text-xs text-zinc-500">
        {t("email.thread", { count: rows.length })}
        {item.thread_guessed ? ` · ${t("email.threadGuessed")}` : ""}
      </p>
      {cards.map((c, k) => (
        // A stack: each card glued to the one before, a little over it (later emails on top).
        <div
          key={c.key}
          class={`relative rounded-md ${k === 0 ? "" : `${OVERLAP} shadow-[0_-1px_3px_-1px_rgb(0_0_0/0.16)]`}`}
          style={{ zIndex: k + 1 }}
        >
          {c.node}
        </div>
      ))}
    </section>
  );
}
